import React, { useState, useEffect } from "react";
import { ConfigProvider, Select } from "antd";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Navbar from "../../components/Navbar/Navbar";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import {
  getPosSessionHistory,
  getPosSessionList,
  getCashierSessions, // ← add this to your SessionHistoryFunctions
} from "../../API  Functions/SessionHistoryFunctions";
import CloseSessionModal from "../../modals/CloseSession_Modal";
import { Drawer } from "antd";
import SelectBox from "../../components/CustomWidgets/SelectBox";
import { DatePicker } from "antd";
import LoadingSpinner from "../../components/Loading";
import { checkPermission } from "../../Utils/permissionHelper";
import SearchBox from "../../components/CustomWidgets/SearchBox";
import { Skeleton } from "@mui/material";
import SessionHistoryTable from "./SessionHistoryTable";
import AllSessionHistory from "./AllSessionData";
import DateFilter from "../../components/ui/DateFilter";
import dayjs from "dayjs";
import ShimmerLoader from "../../loading";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";

const { RangePicker } = DatePicker;

// ─── Skeleton ────────────────────────────────────────────────────────────────
const CashierSessionSkeleton = ({ count = 2 }) => (
  <>
    <div className="flex justify-center">
      <Skeleton variant="text" width={500} height={45} animation="wave" />
    </div>
    <div className="flex justify-center">
      <Skeleton variant="text" width={500} height={45} animation="wave" />
    </div>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-[#F6F6F6] p-4 mb-4 rounded-lg">
        <div className="flex justify-center">
          <Skeleton variant="text" width={200} height={32} animation="wave" />
        </div>
        <div className="grid grid-cols-1 gap-2 mt-2">
          {Array.from({ length: 12 }).map((_, idx) => (
            <div key={idx} className="flex justify-between">
              <Skeleton
                variant="text"
                width={150}
                height={22}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width={90}
                height={22}
                animation="wave"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Skeleton
            variant="rectangular"
            width={140}
            height={36}
            animation="wave"
            sx={{ borderRadius: "6px" }}
          />
        </div>
      </div>
    ))}
  </>
);

// ─── Component ───────────────────────────────────────────────────────────────
const SessionHistory = () => {
  const { t, i18n } = useTranslation();
  const user_id = useSelector((state) => state?.login?.user?.result?.user_id);
  const company_id = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );
  const userData = useSelector((state) => state?.login?.user?.result);
  const isGinne = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);

  // ── Sub-tab ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("user_wise");

  const subTabs = [
    { key: "user_wise", label: t("User Wise Sessions") },
    { key: "all_sessions", label: t("All Sessions") },
  ];

  const sessionOptions = [
    { value: "all", label: t("All Sessions") },
    { value: "opened", label: t("Opened") },
    { value: "closed", label: t("Closed") },
  ];

  // ── User Wise state ────────────────────────────────────────────────────────
  const [showCloseSessionModal, setShowCloseSessionModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [visible, setVisible] = useState(false);
  const [sessionHistoryData, setSessionHistoryData] = useState([]);
  const [drawerData, setDrawerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [drawerRange, setDrawerRange] = useState("current_week");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState("all");
  const [uwData, setUwData] = useState([]);
  const [uwActualData, setUwActualData] = useState([]);
  const [searchSessionQuery, setSearchSessionQuery] = useState("");

  // ── All Sessions state ─────────────────────────────────────────────────────
  const today = dayjs();
  const [allSearch, setAllSearch] = useState("");
  const [allStatus, setAllStatus] = useState("all");
  const [allSelectedRange, setAllSelectedRange] = useState("thisMonth");
  const [allStartDate, setAllStartDate] = useState(
    today.startOf("month").format("YYYY-MM-DD"),
  );
  const [allEndDate, setAllEndDate] = useState(today.format("YYYY-MM-DD"));
  const [allData, setAllData] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allCurrentPage, setAllCurrentPage] = useState(1);
  const [allPageSize, setAllPageSize] = useState(10);
  const [allTotal, setAllTotal] = useState(0);

  const [drawerVisible, setDrawerVisible] = useState(false);
const [drawerSessionData, setDrawerSessionData] = useState(null);
const [drawerSessionLoading, setDrawerSessionLoading] = useState(false);

  // ── User Wise: fetch list ──────────────────────────────────────────────────
  const getSessionList = () => {
    setLoading(true);
    getPosSessionList(user_id)
      .then((res) => {
        if (res?.result?.length > 0) {
          setUwData(res.result);
          setUwActualData(res.result);
        } else {
          setUwData([]);
          setUwActualData([]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    getSessionList();
  }, []);

  // ── User Wise: drawer session history ─────────────────────────────────────
  const getSessionHistory = (
    userId,
    startDate = null,
    endDate = null,
    filterType,
    text,
  ) => {
    const payload = {
      user_id: userId,
      search: text,
      limit: 30,
      offset: 0,
      filter_type: filterType,
      custom_start_date: startDate,
      custom_end_date: endDate,
    };
    getPosSessionHistory(payload)
      .then((res) => {
        setHistoryLoading(false);
        if (
          res?.result[0]?.message === "No data found for the given filters."
        ) {
          setSessionHistoryData([]);
        } else if (res?.result?.length > 0) {
          setSessionHistoryData(res.result);
        }
      })
      .catch((err) => console.error("Error fetching session history:", err));
  };

  const showDrawer = (historyData) => {
    setDrawerData(historyData);
    setVisible(true);
    setHistoryLoading(true);
    getSessionHistory(historyData.user_id, null, null, drawerRange, "");
    setSelectedUserId(historyData.user_id);
  };

  const onClose = () => {
    setVisible(false);
    setSessionHistoryData([]);
    setDrawerRange("current_week");
    setSearchSessionQuery("");
  };

  const handleCloseSessionClick = (sessionId, accessToken) => {
    setSelectedSessionId(sessionId);
    setSelectedToken(accessToken);
    setShowCloseSessionModal(true);
  };

  const handleCloseModal = () => {
    setShowCloseSessionModal(false);
    getSessionList();
  };

  const handleDrawerRangeChange = (range) => {
    const val = range.target.value;
    setDrawerRange(val);
    setSearchSessionQuery("");
    setHistoryLoading(true);
    getSessionHistory(selectedUserId, null, null, val, "");
  };

  const handleRangePickerChange = (dates, dateStrings) => {
    if (dates?.length === 2) {
      setDrawerRange("custom_range");
      setSearchSessionQuery("");
      setHistoryLoading(true);
      getSessionHistory(
        selectedUserId,
        dateStrings[0],
        dateStrings[1],
        "custom_range",
        "",
      );
    }
  };

  const searchUser = (searchText) => {
    setSelectedPaymentTerm("all");
    if (!searchText?.trim()) {
      setUwData(uwActualData);
      return;
    }
    const text = searchText.toLowerCase();
    setUwData(
      uwActualData.filter((user) =>
        (user?.user_name || "").toLowerCase().includes(text),
      ),
    );
  };

  const handleSessionChange = (value) => {
    setSelectedPaymentTerm(value);
    if (value === "all") {
      setUwData(uwActualData);
    } else {
      setUwData(
        uwActualData.filter(
          (item) => item.last_sessions[0]?.session_state === value,
        ),
      );
    }
  };

  // ── All Sessions: fetch ────────────────────────────────────────────────────
  const fetchAllSessions = (
    search = allSearch,
    status = allStatus,
    start = allStartDate,
    end = allEndDate,
    limit = 10,
    offset = 0,
  ) => {
    setAllLoading(true);
    const payload = {
      store_id: company_id,
      start_date: start || "",
      end_date: end || "",
      search: search || "",
      status: status === "all" ? "" : status || "",
      // user_id: userData?.user_id,
      limit: limit,
      offset: offset,
    };
    getCashierSessions(payload)
      .then((res) => {
        const result = res?.result || {};
        setAllData(result?.data || []);
      })
      .catch((err) => {
        console.error("Error fetching all sessions:", err);
        setAllData([]);
      })
      .finally(() => setAllLoading(false));
  };

  // fetch on tab switch to all_sessions
  useEffect(() => {
    if (activeTab === "all_sessions") {
      fetchAllSessions(allSearch, allStatus, allStartDate, allEndDate);
      setAllCurrentPage(1);
    }
  }, [activeTab]);

  const handleAllSearch = (value) => {
    setAllSearch(value);
    setAllCurrentPage(1);
    fetchAllSessions(value, allStatus, allStartDate, allEndDate);
  };

  const handleAllStatusChange = (value) => {
    setAllStatus(value);
    setAllCurrentPage(1);
    fetchAllSessions(allSearch, value, allStartDate, allEndDate);
  };

  const handleAllDateFilter = (start, end) => {
    setAllStartDate(start);
    setAllEndDate(end);
    setAllSearch("");
    setAllStatus("all");
    setAllCurrentPage(1);
    fetchAllSessions("", "all", start, end);
  };

  // ── Print helpers ──────────────────────────────────────────────────────────
  function HTMLData(session) {
    return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>Session Report</title>
    <style>body{font-family:sans-serif;padding:20px;background:#F6F6F6}.session-box{background:#F6F6F6;padding:16px;margin-bottom:16px;border-radius:8px}.session-title{font-size:1.25rem;font-weight:600;text-align:center;margin-bottom:16px}.grid{display:grid;grid-template-columns:1fr;gap:8px}.row{display:flex;justify-content:space-between;font-size:0.875rem}.label{font-weight:400}.label-bold{font-weight:600}</style>
    </head><body><div class="session-box">
    <div class="session-title">${userData.company_name}</div>
    <div class="session-title">${session.session_name}</div>
    <div class="grid">
      <div class="row"><span class="label">${t("Opening Date")}</span><span>${session.opening_date || "--"}</span></div>
      <div class="row"><span class="label">${t("Closing Date")}</span><span>${session.closing_date || "--"}</span></div>
      <div class="row"><span class="label">${t("Status")}</span><span>${session.status || "--"}</span></div>
      <div class="row"><span class="label">${t("Number of Orders")}</span><span>${session.order_count || "0"}</span></div>
      <div class="row"><span class="label-bold">${t("Total Sales")}</span><span>EGP ${Number(session.total_sales || 0).toFixed(2)}</span></div>
      <div class="row"><span class="label">${t("Refunded Amount")}</span><span>EGP ${Number(session.refunded_amt || 0).toFixed(2)}</span></div>
      ${Object.keys(session.all_collection || {})
        .map(
          (pm) =>
            `<div class="row"><span class="label">${t(pm)} ${t("Collection")}</span><span>EGP ${Number(session.all_collection[pm] || 0).toFixed(2)}</span></div>`,
        )
        .join("")}
      <div class="row"><span class="label-bold">${t("Opening Cash")}</span><span>EGP ${Number(session.opening_cash || 0).toFixed(2)}</span></div>
      <div class="row"><span class="label">${t("Cash In")}</span><span>EGP ${Number(session.cash_in || 0).toFixed(2)}</span></div>
      <div class="row"><span class="label">${t("Cash Out")}</span><span>EGP ${Number(session.cash_out || 0).toFixed(2)}</span></div>
      <div class="row"><span class="label-bold">${t("Available Amount")}</span><span>EGP ${Number(session.cash_available || 0).toFixed(2)}</span></div>
    </div></div></body></html>`;
  }

  const renderSessionBox = (session) => {
    const onPrint = () => {
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:absolute;width:0;height:0;border:none";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(HTMLData(session));
      doc.close();
      iframe.contentWindow.focus();
      setTimeout(() => {
        iframe.contentWindow.print();
        document.body.removeChild(iframe);
      }, 1000);
    };

    const collection = session.all_collection || {};
    return (
      <div
        key={session.session_id}
        className="bg-[#F6F6F6] p-4 mb-4 rounded-lg"
      >
        <div className="text-lg text-center font-semibold">
          {(session.session_name || "").replace("POS", t("Cashier"))}
        </div>
        <div className="grid grid-cols-1 gap-2 mt-2">
          {[
            { label: t("Opening Date"), value: session.opening_date || "--" },
            { label: t("Closing Date"), value: session.closing_date || "--" },
            {
              label: t("Status"),
              value: session.status ? t(session.status) : "--",
            },
            { label: t("Number of Orders"), value: session.order_count || "0" },
            {
              label: t("Number of Refunded Orders"),
              value: session.refunded_order || "0",
            },
          ].map(({ label, value }) => (
            <div key={label} className="flex flex-row justify-between">
              <p className="font-normal text-sm">{label}</p>
              <p className="text-sm">{value}</p>
            </div>
          ))}
          <div className="flex flex-row justify-between items-center">
            <div className="flex items-center gap-1">
              <p className="font-semibold text-sm">{t("Total Sales")}</p>
              <Tooltip
                title={t("Total Sales = All Collection - Refunded Amount")}
                arrow
              >
                <InfoOutlinedIcon
                  fontSize="inherit"
                  className="text-gray-500 cursor-pointer w-4 h-4"
                />
              </Tooltip>
            </div>
            <p className="text-sm">{`EGP ${Number(session.total_sales || 0).toFixed(2)}`}</p>
          </div>
          <div className="flex flex-row justify-between">
            <p className="font-normal text-sm">{t("Refunded Amount")}</p>
            <p className="text-sm">{`EGP ${Number(session.refunded_amt || 0).toFixed(2)}`}</p>
          </div>
          {Object.keys(collection).map((pm) => (
            <div key={pm} className="flex flex-row justify-between">
              <p className="font-normal text-sm">{`${t(pm)} ${t("Collection")}`}</p>
              <p className="text-sm">{`EGP ${isNaN(Number(collection[pm])) ? "0.00" : Number(collection[pm]).toFixed(2)}`}</p>
            </div>
          ))}
          <div className="flex flex-row justify-between">
            <p className="font-semibold text-sm">{t("Opening Cash")}</p>
            <p className="text-sm">{`EGP ${Number(session.opening_cash || 0).toFixed(2)}`}</p>
          </div>
          <div className="flex flex-row justify-between">
            <p className="font-normal text-sm">{t("Cash In")}</p>
            <p className="text-sm">{`EGP ${Number(session.cash_in || 0).toFixed(2)}`}</p>
          </div>
          <div className="flex flex-row justify-between">
            <p className="font-normal text-sm">{t("Cash Out")}</p>
            <p className="text-sm">{`EGP ${Number(session.cash_out || 0).toFixed(2)}`}</p>
          </div>
          {session.cash_out_summary?.map((item, idx) => (
            <div
              key={idx}
              className="flex flex-row justify-between items-center"
              style={{ marginTop: "-6px", marginBottom: "-6px" }}
            >
              <div className="flex items-center gap-1">
                <span className="text-gray-700 text-lg">•</span>
                <p className="text-sm text-gray-700 italic">
                  {item.remark || t("Other")}
                </p>
              </div>
              <p className="text-sm text-gray-700 italic">{`EGP ${Number(item.amount || 0).toFixed(2)}`}</p>
            </div>
          ))}
          <div className="flex flex-row justify-between items-center">
            <div className="flex items-center gap-1">
              <p className="font-semibold text-sm">{t("Available Amount")}</p>
              <Tooltip
                title={t(
                  "Available Amount = All Collection + Opening Cash + Cash In - Refunded Amount - Cash Out",
                )}
                arrow
              >
                <InfoOutlinedIcon
                  fontSize="inherit"
                  className="text-gray-500 cursor-pointer w-4 h-4"
                />
              </Tooltip>
            </div>
            <p className="text-sm">{`EGP ${Number(session.cash_available || 0).toFixed(2)}`}</p>
          </div>
        </div>
        <div className="flex justify-end">
          <div
            onClick={() =>
              checkPermission("/pos/sessionHistory", isGinne) && onPrint()
            }
            className="rounded-md bg-primaryBlue text-white text-base font-medium w-36 mt-4 py-1 flex justify-center cursor-pointer"
          >
            {t("Print")}
          </div>
        </div>
      </div>
    );
  };

  const normalizeSessionDetail = (raw) => ({
  ...raw,
  session_name: raw.session_name
    ? String(raw.session_name).replace("POS", t("Cashier"))
    : `${t("Cashier")}/${raw.session_id}`,
  closing_date: raw.closing_date || "--",
  all_collection: raw.all_collection || {},
  cash_out_summary: raw.cash_out_summary || [],
});

 const handleRowClick = async(record) => {
  console.log('record==>',record);
  
  setDrawerVisible(true);
  setDrawerSessionLoading(true);
  setDrawerSessionData(null);
  try{
 const response = await axiosInstance.post(URLS.GET_SESSION_DETAILS, {
    session_id: record
  });
  if(response?.data?.result?.status_code == 200){
      // setDrawerSessionData(response?.data?.result?.data);
 setDrawerSessionData(normalizeSessionDetail(response?.data?.result?.data));
  }
     
  }catch(err){
console.error(err)
  }finally{
  setDrawerSessionLoading(false);

  }

 
    
};

const closeAllSessionDrawer = () => {
  setDrawerVisible(false);
  setDrawerSessionData(null);
};

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {loading && <LoadingSpinner />}
      <div className="flex w-full">
        <div className="flex flex-1 min-h-[99vh] flex-col bg-white">
          <Navbar heading="Session History" />
          <div className="mx-2 sm:mx-5">
            {/* Sub-tabs */}
            <div className="sticky top-0 z-40 bg-white border-b px-4 py-3 flex gap-4 overflow-x-auto mt-2">
              {subTabs.map((tab) => (
                <div
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`cursor-pointer px-4 py-2 rounded-md font-medium whitespace-nowrap transition-all
                    ${
                      activeTab === tab.key
                        ? "bg-[#E6F0FF] text-[#1D4ED8] border border-[#1D4ED8]"
                        : "text-[#6B7280] hover:bg-gray-100"
                    }`}
                >
                  {tab.label}
                </div>
              ))}
            </div>

            {/* ── User Wise Sessions ── */}
            {activeTab === "user_wise" && (
              <>
                <div className="mt-2 flex w-full flex-col gap-2 sm:mt-5 md:w-1/2 md:flex-row md:items-end md:gap-x-3">
                  <SearchBox
                    searchResult={searchUser}
                    placeholder={t("Search Users")}
                  />
                  <Select
                    style={{ width: "100%" }}
                    placeholder={t("Select Session Status")}
                    className="h-10 mt-2"
                    value={selectedPaymentTerm}
                    onChange={handleSessionChange}
                  >
                    {sessionOptions.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {t(opt.label)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div className="mt-3">
                  <SessionHistoryTable
                    additionalData={uwData}
                    showDrawer={showDrawer}
                    handleCloseSessionClick={handleCloseSessionClick}
                  />
                </div>
              </>
            )}

            {/* ── All Sessions ── */}
            {activeTab === "all_sessions" && (
              <>
                {/* Search + Status */}
                <div className="mt-2 flex w-full flex-col gap-2 sm:mt-5 md:w-8/12 md:flex-row md:items-end md:gap-x-3 ">
                  <SearchBox
                    searchResult={handleAllSearch}
                    placeholder={t("Search by session, user name or email")}
                  />
                  <Select
                    style={{ width: "30%" }}
                    placeholder={t("Select Session Status")}
                    className="h-10 mt-2"
                    value={allStatus}
                    onChange={handleAllStatusChange}
                  >
                    {sessionOptions.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {t(opt.label)}
                      </Select.Option>
                    ))}
                  </Select>
                  <div className="">
                    <DateFilter
                      setSelectedRange={setAllSelectedRange}
                      selectedRange={allSelectedRange}
                      SubmitFilter={(start, end) =>
                        handleAllDateFilter(start, end)
                      }
                    />
                  </div>
                </div>

                {/* Date Filter */}

                {/* Table */}
                <div className="mt-3">
                  {/* {allLoading ? 
                    <ShimmerLoader />
                   :  */}
                  <AllSessionHistory
                    additionalData={allData}
                    loading={allLoading}
                    // onTableChange={handleAllTableChange}
                    pagination={(values) => {
                      fetchAllSessions(
                        allSearch,
                        allStatus,
                        allStartDate,
                        allEndDate,
                        values.limit,
                        values.offset,
                      );
                    }}
                    showDrawer={showDrawer}
                    handleCloseSessionClick={handleCloseSessionClick}
                    onRowClick={handleRowClick}  

                  />
                  {/* } */}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Close Session Modal */}
      {showCloseSessionModal && (
        <CloseSessionModal
          closeModal={handleCloseModal}
          sessionId={selectedSessionId}
          accessToken={selectedToken}
        />
      )}

      {/* Drawer */}
      <Drawer
        title={`${drawerData?.user_name} (S-${drawerData?.session_count}, O-${drawerData?.order_count})`}
        placement="right"
        width={
          typeof window !== "undefined" && window.innerWidth < 768 ? "95%" : 500
        }
        onClose={onClose}
        visible={visible}
      >
        {historyLoading ? (
          <CashierSessionSkeleton />
        ) : (
          <div>
            <div className="mb-4">
              <SelectBox
                value={t(drawerRange)}
                options={[
                  { value: "current_week", label: t("Current Week") },
                  { value: "last_week", label: t("Last Week") },
                  { value: "custom_range", label: t("Custom Range") },
                ]}
                onChange={handleDrawerRangeChange}
                placeholder={t("select_date_range")}
              />
              {drawerRange === "custom_range" && (
                <ConfigProvider
                  direction={i18n.language === "ar" ? "rtl" : "ltr"}
                >
                  <RangePicker
                    className="w-full mt-2 h-10 range-picker-placeholder"
                    style={{ minWidth: 220 }}
                    onChange={handleRangePickerChange}
                    placeholder={[t("Start Date"), t("End Date")]}
                  />
                </ConfigProvider>
              )}
              <div className="mt-3 w-full">
                <SearchBox
                  value={searchSessionQuery}
                  width="500px"
                  searchResult={(values) => {
                    setSearchSessionQuery(values);
                    getSessionHistory(
                      selectedUserId,
                      null,
                      null,
                      drawerRange,
                      values,
                    );
                  }}
                  placeholder={t("Search Session")}
                />
              </div>
            </div>
            {sessionHistoryData.length > 0 ? (
              sessionHistoryData.map((session) => renderSessionBox(session))
            ) : (
              <p className="text-center mt-5">
                {t("No session data available")}
              </p>
            )}
          </div>
        )}
      </Drawer>
      {/* All Sessions Detail Drawer */}
<Drawer
  title={t("Session Details")}
  placement="right"
  width={typeof window !== "undefined" && window.innerWidth < 768 ? "95%" : 500}
  onClose={closeAllSessionDrawer}
  visible={drawerVisible}
>
  {drawerSessionLoading ? (
    <CashierSessionSkeleton count={1} />
  ) : drawerSessionData ? (
    renderSessionBox(drawerSessionData)
  ) : (
    <p className="text-center mt-5">{t("No session data available")}</p>
  )}
</Drawer>
    </>
  );
};

export default SessionHistory;
